import { useQuery } from '@tanstack/react-query';
import { apiClient } from '@/services/api';
import { needsMatchPolling } from '@/utils/matchPolling';
import type { LeagueMatchesResponse } from '@/types/league';

const POLL_INTERVAL_MS = 60_000;

/**
 * A league's whole season of matches. Every league tab calls this hook, so
 * they share one cache entry, one request and one polling timer.
 *
 * Live updates: the query polls every 60s while a match is live or kicks off
 * within five minutes (matching the backend's own 60s upstream poll) and stops
 * otherwise.
 */
export function useLeagueMatches(leagueId: string) {
  return useQuery<LeagueMatchesResponse>({
    queryKey: ['league-matches', leagueId],
    queryFn: async () => (await apiClient.getLeagueMatches(leagueId, { all: true })).data,
    enabled: !!leagueId,
    // No keepPreviousData: the only key part is the league, and another
    // league's season must never show under this league's header.
    refetchInterval: (query) => (needsMatchPolling(query.state.data?.data ?? []) ? POLL_INTERVAL_MS : false),
    refetchIntervalInBackground: false,
  });
}
